import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { SpaBadge } from '@/components/ui/spa-badge';
import { SectorBadge } from '@/components/ui/sector-badge';
import { TypeBadge } from '@/components/ui/type-badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Building2, ArrowLeft, Globe, ExternalLink, CheckCircle2, Flag, MapPin, Calendar } from 'lucide-react';
import { format } from 'date-fns';

const FLAG_MAP: Record<string, string> = {
  'United States': '🇺🇸',
  'DR Congo': '🇨🇩',
  'South Africa': '🇿🇦',
  'Australia': '🇦🇺',
  'Canada': '🇨🇦',
  'United Kingdom': '🇬🇧',
  'Switzerland': '🇨🇭',
  'China': '🇨🇳',
};

const TYPE_LABELS: Record<string, { key: string; className: string }> = {
  us_company: { key: 'companies.usInvestor', className: 'bg-primary/10 text-primary border-primary/20' },
  congolese_sme: { key: 'companies.localSme', className: 'bg-secondary/10 text-secondary border-secondary/20' },
  multinational: { key: 'companies.multinational', className: 'bg-spa-governance/10 text-spa-governance border-spa-governance/20' },
  other: { key: 'companyProfile.types.other', className: 'bg-muted text-muted-foreground border-border' },
};

const TYPE_TAGLINES: Record<string, string> = {
  us_company: 'U.S. Investor / Company',
  congolese_sme: 'Local Supplier / SME',
  multinational: 'Multinational Corporation',
  other: 'Organization',
};

interface Company {
  id: string;
  name: string;
  logo_url: string | null;
  country: string;
  company_type: string | null;
  sectors: string[];
  spa_linked: boolean;
  spa_role_description: string | null;
  verified: boolean;
  description: string | null;
  website: string | null;
  created_at: string;
}

interface Opportunity {
  id: string;
  title: string;
  type: 'job' | 'tender' | 'jv' | 'training' | 'service_offer';
  sector: 'mining' | 'processing' | 'infrastructure' | 'industrial' | 'services' | 'other';
  country: string;
  city: string | null;
  remote: boolean;
  spa_linked: boolean;
  spa_pillar: 'critical_minerals' | 'industrialization' | 'infrastructure' | 'governance' | null;
  deadline: string | null;
  description: string;
  created_at: string;
}

export default function CompanyDetail() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [company, setCompany] = useState<Company | null>(null);
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const [companyRes, oppsRes] = await Promise.all([
        supabase.from('companies').select('*').eq('id', id).single(),
        supabase.from('opportunities').select('*').eq('company_id', id!).eq('status', 'open').order('created_at', { ascending: false }),
      ]);

      if (companyRes.error) throw companyRes.error;
      const c = companyRes.data as any;
      setCompany({ ...c, sectors: Array.isArray(c.sectors) ? c.sectors : [] });
      setOpportunities((oppsRes.data as unknown as Opportunity[]) || []);
    } catch (error) {
      console.error('Error fetching company:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="container py-8">
          <Skeleton className="mb-6 h-8 w-48" />
          <div className="grid gap-8 lg:grid-cols-3">
            <div className="lg:col-span-2 space-y-6">
              <div className="flex items-center gap-6">
                <Skeleton className="h-32 w-32 rounded-xl" />
                <div className="space-y-3 flex-1">
                  <Skeleton className="h-8 w-3/4" />
                  <Skeleton className="h-5 w-1/2" />
                  <Skeleton className="h-5 w-1/3" />
                </div>
              </div>
              <Skeleton className="h-40 w-full" />
            </div>
            <div className="space-y-6">
              <Skeleton className="h-48 w-full" />
              <Skeleton className="h-32 w-full" />
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!company) {
    return (
      <Layout>
        <div className="container py-12 text-center">
          <Building2 className="mx-auto mb-4 h-16 w-16 text-muted-foreground/40" />
          <h1 className="text-2xl font-bold">Company not found</h1>
          <Button asChild variant="outline" className="mt-4">
            <Link to="/companies">{t('common.back')}</Link>
          </Button>
        </div>
      </Layout>
    );
  }

  const flag = FLAG_MAP[company.country] || '🌍';
  const sectors = company.sectors as Array<'mining' | 'processing' | 'infrastructure' | 'industrial' | 'services' | 'other'>;
  const typeConfig = company.company_type ? TYPE_LABELS[company.company_type] : null;
  const tagline = company.company_type ? TYPE_TAGLINES[company.company_type] || '' : '';
  const displayedOpps = opportunities.slice(0, 5);
  const memberSince = format(new Date(company.created_at), 'MMMM yyyy');

  return (
    <Layout>
      <div className="container py-8">
        {/* Back */}
        <Button variant="ghost" className="mb-6 gap-2" onClick={() => navigate('/companies')}>
          <ArrowLeft className="h-4 w-4" />
          ← {t('companies.title')}
        </Button>

        <div className="grid gap-8 lg:grid-cols-3">
          {/* Main */}
          <div className="lg:col-span-2 space-y-6">
            {/* Header */}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:gap-6">
              {company.logo_url ? (
                <img src={company.logo_url} alt={company.name} className="h-32 w-32 rounded-xl object-cover" />
              ) : (
                <div className="flex h-32 w-32 items-center justify-center rounded-xl bg-muted">
                  <Building2 className="h-16 w-16 text-muted-foreground" />
                </div>
              )}
              <div className="space-y-2">
                <div className="flex flex-wrap items-center gap-3">
                  <h1 className="text-3xl font-bold">{company.name}</h1>
                  {company.verified && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-secondary/10 px-2.5 py-1 text-xs font-semibold text-secondary">
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      {t('companies.verifiedMember')}
                    </span>
                  )}
                </div>

                {typeConfig && (
                  <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${typeConfig.className}`}>
                    {t(typeConfig.key)}
                  </span>
                )}
                {tagline && <p className="text-sm text-muted-foreground">{tagline}</p>}

                <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                  <span>{flag} {company.country}</span>
                  {company.website && (
                    <a
                      href={company.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-primary hover:underline"
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                      {t('company.profile.website')}
                    </a>
                  )}
                </div>

                {sectors.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {sectors.map(s => <SectorBadge key={s} sector={s} />)}
                  </div>
                )}

                {company.spa_linked && <SpaBadge />}
              </div>
            </div>

            {/* Description */}
            {company.description && (
              <Card>
                <CardContent className="p-6">
                  <div className="whitespace-pre-wrap text-sm">{company.description}</div>
                </CardContent>
              </Card>
            )}

            {/* SPA Involvement */}
            {company.spa_role_description && (
              <Card className="border-accent/50">
                <CardContent className="p-6">
                  <h3 className="mb-2 font-semibold text-accent-foreground flex items-center gap-2">
                    <SpaBadge size="sm" />
                    {t('companies.spaInvolvement')}
                  </h3>
                  <p className="text-sm whitespace-pre-wrap">{company.spa_role_description}</p>
                </CardContent>
              </Card>
            )}

            {/* Opportunities */}
            <div>
              <h2 className="mb-4 text-xl font-semibold">
                {t('opportunities.directory.title')} — {company.name}
              </h2>
              {displayedOpps.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t('opportunities.directory.noResults')}</p>
              ) : (
                <div className="grid gap-4 sm:grid-cols-2">
                  {displayedOpps.map(opp => (
                    <Card key={opp.id} className={`transition-shadow hover:shadow-md ${opp.spa_linked ? 'spa-card-highlight' : ''}`}>
                      <CardContent className="p-4">
                        <div className="mb-2 flex items-start justify-between gap-2">
                          <TypeBadge type={opp.type} />
                          {opp.spa_linked && <SpaBadge pillar={opp.spa_pillar || undefined} size="sm" />}
                        </div>
                        <h3 className="font-semibold line-clamp-2 text-sm">{opp.title}</h3>
                        <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
                          <MapPin className="h-3 w-3" />
                          {opp.city ? `${opp.city}, ` : ''}{opp.country}
                        </p>
                        <SectorBadge sector={opp.sector} showIcon={false} className="mt-2" />
                        {opp.deadline && (
                          <p className="mt-2 text-xs text-muted-foreground">
                            <Calendar className="mr-1 inline h-3 w-3" />
                            {format(new Date(opp.deadline), 'MMM d, yyyy')}
                          </p>
                        )}
                      </CardContent>
                      <CardFooter className="border-t p-3">
                        <Button asChild variant="outline" size="sm" className="w-full">
                          <Link to={`/opportunities/${opp.id}`}>{t('opportunities.directory.viewDetails')}</Link>
                        </Button>
                      </CardFooter>
                    </Card>
                  ))}
                </div>
              )}
              {opportunities.length > 5 && (
                <Button asChild variant="link" className="mt-4">
                  <Link to="/opportunities">
                    {t('common.view')} {opportunities.length} {t('opportunities.directory.title')} →
                  </Link>
                </Button>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">{t('aboutSpa.overview.title')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                {company.company_type && (
                  <div>
                    <span className="text-muted-foreground">{t('companies.filterType')}: </span>
                    <span className="font-medium">
                      {company.company_type === 'us_company' ? t('companies.usInvestor')
                        : company.company_type === 'congolese_sme' ? t('companies.localSme')
                        : company.company_type === 'multinational' ? t('companies.multinational')
                        : t('sectors.other')}
                    </span>
                  </div>
                )}
                <div>
                  <span className="text-muted-foreground">{t('companies.filterCountry')}: </span>
                  <span className="font-medium">{flag} {company.country}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">{t('companies.memberSince')}: </span>
                  <span className="font-medium">{memberSince}</span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">{t('nav.settings')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {company.website && (
                  <Button asChild variant="outline" className="w-full gap-2">
                    <a href={company.website} target="_blank" rel="noopener noreferrer">
                      <Globe className="h-4 w-4" />
                      {t('company.profile.website')}
                    </a>
                  </Button>
                )}
                <button className="flex w-full items-center justify-center gap-2 text-sm text-muted-foreground hover:text-foreground">
                  <Flag className="h-4 w-4" />
                  {t('opportunities.detail.report')}
                </button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
}
